import React, { useState, useMemo, useEffect, useRef } from 'react';
import { FiEye, FiX, FiCalendar, FiUser, FiDollarSign, FiSearch, FiFilter, FiXCircle } from 'react-icons/fi';
import Swal from 'sweetalert2';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import { useUserSubscriptions, useUserSubscription } from '../hooks/useUserSubscriptions';
import { apiClient } from '../utils/api';
import { useSearchParams } from 'react-router-dom';

// User Subscription Details Modal Component
const UserSubscriptionDetailsModal = ({ isOpen, onClose, userSubscriptionId }) => {
  const { data: userSubscriptionResponse, isLoading } = useUserSubscription(userSubscriptionId);

  if (!isOpen || !userSubscriptionId) return null;

  const userSubscription = userSubscriptionResponse?.data;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-900">User Subscription Details</h3>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-700"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>
        
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            <span className="ml-2 text-gray-600">Loading...</span>
          </div>
        ) : userSubscription ? (
          <div className="space-y-6">
            {/* User Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <FiUser className="mr-2" />
                User Information
              </h4>
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  {userSubscription.user.image ? (
                    <img 
                      src={userSubscription.user.image} 
                      alt={userSubscription.user.name}
                      className="h-16 w-16 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-16 w-16 bg-gray-200 rounded-full flex items-center justify-center">
                      <FiUser className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                </div>
                <div>
                  <h5 className="text-lg font-medium text-gray-900">{userSubscription.user.name}</h5>
                  <p className="text-gray-600">{userSubscription.user.email}</p>
                  <p className="text-sm text-gray-500">ID: {userSubscription.user.id}</p>
                </div>
              </div>
            </div>

            {/* Subscription Information */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <FiDollarSign className="mr-2" />
                Subscription Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Subscription Name</label>
                  <p className="text-gray-900 font-medium">{userSubscription.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    userSubscription.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {userSubscription.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Currency</label>
                  <p className="text-gray-900">{userSubscription.currency_code}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Profile Month Views</label>
                  <p className="text-gray-900">{userSubscription.profile_month_views}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Events Per Month</label>
                  <p className="text-gray-900">{userSubscription.events_per_month}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Monthly Price</label>
                  <p className="text-gray-900">${userSubscription.monthly_price || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Yearly Price</label>
                  <p className="text-gray-900">${userSubscription.yearly_price || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Start Date</label>
                  <p className="text-gray-900">{formatDate(userSubscription.start_date)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">End Date</label>
                  <p className="text-gray-900">{formatDate(userSubscription.end_date)}</p>
                </div>
              </div>
            </div>

            {/* Transaction Information */}
            {userSubscription.transaction && (
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <FiDollarSign className="mr-2" />
                  Transaction Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Transaction ID</label>
                    <p className="text-gray-900">{userSubscription.transaction.id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Reference</label>
                    <p className="text-gray-900 font-mono text-sm">{userSubscription.transaction.reference}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Short Reference</label>
                    <p className="text-gray-900 font-mono text-sm">{userSubscription.transaction.reference_shorted}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Total Amount</label>
                    <p className="text-gray-900">${userSubscription.transaction.total} {userSubscription.transaction.currency_code}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      userSubscription.transaction.status === 'completed' ? 'bg-green-100 text-green-800' :
                      userSubscription.transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {userSubscription.transaction.status_label || userSubscription.transaction.status}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Type</label>
                    <p className="text-gray-900">{userSubscription.transaction.type_label || userSubscription.transaction.type}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Created At</label>
                    <p className="text-gray-900">{formatDate(userSubscription.transaction.created_at)}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center text-gray-500 py-8">
            User subscription not found
          </div>
        )}
      </div>
    </div>
  );
};

const UserSubscriptions = ({ user }) => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedUserSubscription, setSelectedUserSubscription] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [subs, setSubs] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);
  
  // Refs for input fields
  const userSelectRef = useRef(null);
  const subscriptionSelectRef = useRef(null);
  const statusRef = useRef(null);
  const searchRef = useRef(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const initialUserId = searchParams.get('user_id') || '';
  const initialSubscriptionId = searchParams.get('subscription_id') || '';
  const initialIsActive = searchParams.get('is_active') || '';
  const initialSearch = searchParams.get('search') || '';

  const [filters, setFilters] = useState({
    user_id: initialUserId,
    subscription_id: initialSubscriptionId,
    is_active: initialIsActive,
    search: initialSearch
  });

  useEffect(() => {
    const loadUsers = async () => {
      setUsersLoading(true);
      try {
        const res = await apiClient.getUsersDropdown('');
        setUsers(res.data || []);
      } catch (err) {
        console.error('Failed to load users dropdown', err);
      } finally {
        setUsersLoading(false);
      }
    };
    const loadSubs = async () => {
      setSubsLoading(true);
      try {
        const res = await apiClient.getSubscriptionsDropdown();
        setSubs(res.data || []);
      } catch (err) {
        console.error('Failed to load subscriptions dropdown', err);
      } finally {
        setSubsLoading(false);
      }
    };
    loadUsers();
    loadSubs();
  }, []);

  // After options load, set select values from filters
  useEffect(() => {
    if (userSelectRef.current && !usersLoading) {
      userSelectRef.current.value = filters.user_id || '';
    }
  }, [usersLoading, filters.user_id]);

  useEffect(() => {
    if (subscriptionSelectRef.current && !subsLoading) {
      subscriptionSelectRef.current.value = filters.subscription_id || '';
    }
  }, [subsLoading, filters.subscription_id]);

  // Set status and search input values from filters
  useEffect(() => {
    if (statusRef.current) {
      statusRef.current.value = filters.is_active || '';
    }
    if (searchRef.current) {
      searchRef.current.value = filters.search || '';
    }
  }, [filters.is_active, filters.search]);

  // Fetch user subscriptions using React Query
  const {
    data: userSubscriptionsResponse,
    isLoading,
    error,
    isFetching,
  } = useUserSubscriptions(pagination.current, pagination.pageSize, filters);

  console.log("userSubscriptionsResponse", userSubscriptionsResponse);
  console.log("Current filters:", filters);
  console.log("Current pagination:", pagination);

  // Memoized user subscriptions data
  const { userSubscriptionsData, totalRecords } = useMemo(() => {
    if (!userSubscriptionsResponse) {
      return { userSubscriptionsData: [], totalRecords: 0 };
    }

    return {
      userSubscriptionsData: userSubscriptionsResponse.data.data || [],
      totalRecords: userSubscriptionsResponse.total || 0
    };
  }, [userSubscriptionsResponse]);

  // Get error message from query
  const errorMessage = error?.message;

  // Table columns configuration
  const columns = [
    {
      key: 'id',
      title: 'ID',
      sortable: true,
    },
    {
      key: 'user',
      title: 'User',
      render: (value) => (
        <div className="flex items-center space-x-2">
          {value.image ? (
            <img 
              src={value.image} 
              alt={value.name} 
              className="h-8 w-8 rounded-full object-cover"
            />
          ) : (
            <div className="h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center">
              <FiUser className="h-4 w-4 text-gray-500" />
            </div>
          )}
          <div>
            <div className="font-medium text-gray-900">{value.name}</div>
            <div className="text-sm text-gray-500">ID: {value.id}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'name',
      title: 'Subscription Name',
      sortable: true,
    },
    {
      key: 'currency_code',
      title: 'Currency',
      sortable: true,
    },
    {
      key: 'profile_month_views',
      title: 'Profile Views',
      sortable: true,
    },
    {
      key: 'events_per_month',
      title: 'Events/Month',
      sortable: true,
    },
    {
      key: 'start_date',
      title: 'Start Date',
      render: (value) => (
        <div className="text-sm text-gray-900">
          {new Date(value).toLocaleDateString()}
        </div>
      ),
    },
    {
      key: 'end_date',
      title: 'End Date',
      render: (value) => (
        <div className="text-sm text-gray-900">
          {new Date(value).toLocaleDateString()}
        </div>
      ),
    },
    {
      key: 'is_active',
      title: 'Status',
      render: (value) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'total_amount',
      title: 'Total Amount',
      render: (value, row) => (
        <div className="text-sm font-medium text-gray-900">
          ${value} {row.currency_code}
        </div>
      ),
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, row) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleViewDetails(row)}
            className="text-blue-600 hover:text-blue-800 p-1"
            title="View Details"
          >
            <FiEye className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  // Breadcrumbs
  const breadcrumbs = [
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'User Subscriptions', href: '/dashboard/user-subscriptions' },
  ];

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, current: page }));
  };

  const handlePageSizeChange = (pageSize, current) => {
    setPagination(prev => ({ ...prev, pageSize, current }));
  };

  const handleViewDetails = (userSubscription) => {
    setSelectedUserSubscription(userSubscription);
    setShowDetailsModal(true);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedUserSubscription(null);
  };



  const handleClearFilters = () => {
    const cleared = {
      user_id: '',
      subscription_id: '',
      is_active: '',
      search: ''
    };
    setFilters(cleared);
    setPagination(prev => ({ ...prev, current: 1 }));
    setSearchParams({});
  };

  const handleSearch = () => {
    // Get values from input fields using refs
    const user_id = userSelectRef.current?.value || '';
    const subscription_id = subscriptionSelectRef.current?.value || '';
    const is_active = statusRef.current?.value || '';
    const search = searchRef.current?.value || '';

    const newFilters = { user_id, subscription_id, is_active, search };
    setFilters(newFilters);

    // Sync to URL
    const params = new URLSearchParams();
    if (user_id) params.set('user_id', user_id);
    if (subscription_id) params.set('subscription_id', subscription_id);
    if (is_active) params.set('is_active', is_active);
    if (search) params.set('search', search);
    setSearchParams(params);

    setIsSearching(true);
    setPagination(prev => ({ ...prev, current: 1 }));
    setTimeout(() => setIsSearching(false), 800);
  };

  // Monitor when search is triggered
  useEffect(() => {
    console.log('API call triggered with:', { pagination, filters });
  }, [pagination.current, filters]);

  // Initialize filters from URL search params
  useEffect(() => {
    const initialUser = searchParams.get('user_id') || '';
    const initialSub = searchParams.get('subscription_id') || '';
    const initialStatus = searchParams.get('is_active') || '';
    const initialSearch = searchParams.get('search') || '';

    setFilters(prev => ({ ...prev, user_id: initialUser, subscription_id: initialSub, is_active: initialStatus, search: initialSearch }));
  }, [searchParams]);

  // Set select/input values after options load
  useEffect(() => {
    if (users.length > 0 && userSelectRef.current) {
      userSelectRef.current.value = filters.user_id;
    }
    if (subs.length > 0 && subscriptionSelectRef.current) {
      subscriptionSelectRef.current.value = filters.subscription_id;
    }
    if (statusRef.current) {
      statusRef.current.value = filters.is_active;
    }
    if (searchRef.current) {
      searchRef.current.value = filters.search;
    }
  }, [filters.user_id, filters.subscription_id, filters.is_active, filters.search, users, subs]);

  // Handle loading state
  if (isLoading) {
    return (
      <Layout user={user}>
        <div className="space-y-0">
          <PageHeader
            title="User Subscriptions"
            description="Manage user subscription data"
            breadcrumbs={breadcrumbs}
            user={user}
          />
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            <span className="ml-2 text-gray-600">Loading user subscriptions...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-0">
        <PageHeader
          title="User Subscriptions"
          description="Manage user subscription data"
          breadcrumbs={breadcrumbs}
        />

        {/* Filters Section */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Filters</h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  <FiFilter className="mr-2" />
                  {showFilters ? 'Hide' : 'Show'} Filters
                </button>
                <button
                  onClick={handleClearFilters}
                  className="flex items-center px-3 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100"
                >
                  <FiXCircle className="mr-2" />
                  Clear
                </button>
              </div>
            </div>
          </div>

          {showFilters && (
            <div className="p-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
                  <select
                    ref={userSelectRef}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={filters.user_id || ''}
                    onChange={handleSearch}
                  >
                    <option value="">All Users</option>
                    {usersLoading ? (
                      <option value="" disabled>Loading...</option>
                    ) : (
                      (users || []).map(u => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))
                    )}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Tip: start typing to jump to a user</p>
                 </div>
                 <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subscription</label>
                  <select
                    ref={subscriptionSelectRef}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={filters.subscription_id || ''}
                    onChange={handleSearch}
                  >
                    <option value="">All Subscriptions</option>
                    {subsLoading ? (
                      <option value="" disabled>Loading...</option>
                    ) : (
                      (subs || []).map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))
                    )}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Tip: type to jump to a plan</p>
                 </div>
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                   <select
                     ref={statusRef}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                     value={filters.is_active || ''}
                     onChange={handleSearch}
                   >
                     <option value="">All</option>
                     <option value="active">Active</option>
                     <option value="inactive">Inactive</option>
                   </select>
                 </div>
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                   <div className="flex">
                     <input
                       ref={searchRef}
                       type="text"
                       placeholder="Search..."
                       className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                       value={filters.search || ''}
                       onChange={handleSearch}
                     />
                     <button
                       onClick={handleSearch}
                       className="px-4 py-2 bg-primary-500 text-white rounded-r-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
                     >
                       <FiSearch className="h-4 w-4" />
                     </button>
                  </div>
                 </div>
              </div>
              
                             {/* Search Button */}
               <div className="flex justify-center mt-4">
                 <button
                   onClick={handleSearch}
                   disabled={isSearching}
                   className={`px-6 py-2 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 font-medium ${
                     isSearching 
                       ? 'bg-gray-400 cursor-not-allowed' 
                       : 'bg-primary-500 hover:bg-primary-600'
                   }`}
                 >
                   {isSearching ? (
                     <>
                       <div className="animate-spin inline mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                       Searching...
                     </>
                   ) : (
                     <>
                       <FiSearch className="inline mr-2 h-4 w-4" />
                       Search
                     </>
                   )}
                 </button>
               </div>
            </div>
          )}
        </div>

        {errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
            <div className="text-red-800">{errorMessage}</div>
          </div>
        )}

        <DataTable
          data={userSubscriptionsData}
          columns={columns}
          loading={isLoading}
          filterable={false}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: totalRecords,
          }}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          emptyMessage="No user subscriptions found"
        />

        {/* User Subscription Details Modal */}
        <UserSubscriptionDetailsModal
          isOpen={showDetailsModal}
          onClose={handleCloseDetailsModal}
          userSubscriptionId={selectedUserSubscription?.id}
        />
      </div>
    </Layout>
  );
};

export default UserSubscriptions;
